'use client'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Play,
  MapPin,
  Clock,
  Calendar,
  TrendingUp,
  DollarSign,
  Users,
  Music,
  Sparkles,
  ArrowUpRight,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts'
import { useTranslations } from 'next-intl'
import type { DemoEvent } from '../demo-types'

// ─── Fake performance history data ───────────────────────────────────

const PAST_EVENTS = [
  { name: 'Mar 1', tips: 18, requests: 12, visitors: 22 },
  { name: 'Mar 4', tips: 24, requests: 15, visitors: 28 },
  { name: 'Mar 7', tips: 21, requests: 18, visitors: 31 },
  { name: 'Mar 8', tips: 32, requests: 22, visitors: 35 },
  { name: 'Mar 11', tips: 28, requests: 19, visitors: 33 },
  { name: 'Mar 14', tips: 38, requests: 26, visitors: 42 },
  { name: 'Mar 15', tips: 45, requests: 31, visitors: 48 },
]

const TIP_HISTORY = [
  { name: 'Feb', amount: 42 },
  { name: 'Week 1', amount: 58 },
  { name: 'Week 2', amount: 64 },
  { name: 'Week 3', amount: 78 },
  { name: 'Week 4', amount: 95 },
  { name: 'This wk', amount: 112 },
]

const INSIGHTS = [
  { textKey: 'tipsUpPercent', trend: 'up' as const },
  { textKey: 'audienceGrew', trend: 'up' as const },
  { textKey: 'requestsPeakTip', trend: 'tip' as const },
]

// ─── Component ───────────────────────────────────────────────────────

interface GoLiveViewProps {
  event: DemoEvent
  visitorCount: number
  onGoLive: () => void
}

export function GoLiveView({ event, visitorCount, onGoLive }: GoLiveViewProps) {
  const t = useTranslations('demo')
  return (
    <div className="h-full flex flex-col bg-zinc-950 text-white overflow-auto">
      <div className="flex-1 p-3 sm:p-5 space-y-4">
        {/* Event Header + Go Live */}
        <Card data-coach="artist-view-label" className="border-purple-500/30 bg-gradient-to-br from-purple-950/40 via-zinc-900/80 to-fuchsia-950/40">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-4 w-4 text-purple-400 shrink-0" />
                  <span className="text-xs text-purple-400 font-medium uppercase tracking-wide">{t('tonightsEventArtist')}</span>
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-white truncate">{event.title}</h2>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                  {event.venue && (
                    <span className="flex items-center gap-1 text-xs text-zinc-400">
                      <MapPin className="h-3 w-3" />
                      {event.venue}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-xs text-zinc-400">
                    <Clock className="h-3 w-3" />
                    {event.startTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
              </div>
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/40 text-xs shrink-0">
                {t('ready')}
              </Badge>
            </div>

            {visitorCount > 0 && (
              <div className="flex items-center gap-1.5 mb-3">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-xs text-emerald-400/80">
                  {t('visitorsWaiting', { count: visitorCount })}
                </span>
              </div>
            )}

            <Button
              size="lg"
              onClick={onGoLive}
              data-coach="go-live-button"
              className="w-full h-14 text-base font-bold bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 hover:from-purple-500 hover:via-fuchsia-500 hover:to-pink-500 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 transition-all hover:scale-[1.02] rounded-xl"
            >
              <Play className="h-5 w-5 mr-2" fill="currentColor" />
              {t('goLive')}
            </Button>
          </CardContent>
        </Card>

        {/* Performance Insights */}
        <div className="space-y-1.5">
          {INSIGHTS.map((insight, i) => (
            <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-zinc-900/50 border border-zinc-800/50">
              {insight.trend === 'up' ? (
                <ArrowUpRight className="h-3.5 w-3.5 text-emerald-400 shrink-0 mt-0.5" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
              )}
              <p className="text-xs text-zinc-300 leading-relaxed">{t(insight.textKey)}</p>
            </div>
          ))}
        </div>

        {/* Audience Growth Chart */}
        <Card className="border-zinc-800 bg-zinc-900/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-400" />
                <span className="text-sm font-semibold text-white">{t('audienceAndRequests')}</span>
              </div>
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">
                <TrendingUp className="h-2.5 w-2.5 mr-1" />
                +38%
              </Badge>
            </div>
            <div className="h-28">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={PAST_EVENTS} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="visitorGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(96 165 250)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="rgb(96 165 250)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="requestGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(167 139 250)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="rgb(167 139 250)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <RechartsTooltip
                    contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#a1a1aa' }}
                  />
                  <Area type="monotone" dataKey="visitors" stroke="rgb(96 165 250)" fill="url(#visitorGradient)" strokeWidth={2} name="Visitors" />
                  <Area type="monotone" dataKey="requests" stroke="rgb(167 139 250)" fill="url(#requestGradient)" strokeWidth={2} name="Requests" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2">
              <span className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> {t('visitors')}
              </span>
              <span className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                <span className="h-1.5 w-1.5 rounded-full bg-violet-400" /> {t('requests')}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Tips Trend Chart */}
        <Card className="border-zinc-800 bg-zinc-900/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-emerald-400" />
                <span className="text-sm font-semibold text-white">{t('tipsEarned')}</span>
              </div>
              <span className="text-xs text-emerald-400 font-medium">{t('tipsThisWeek')}</span>
            </div>
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={TIP_HISTORY} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                  <RechartsTooltip
                    contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#a1a1aa' }}
                    formatter={(value) => [`$${value}`, 'Tips']}
                  />
                  <Bar dataKey="amount" fill="rgb(52 211 153)" radius={[4, 4, 0, 0]} name="Tips" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats Summary */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-zinc-900/50 border border-zinc-800 p-3 text-center">
            <Music className="h-4 w-4 text-violet-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">7</p>
            <p className="text-[10px] text-zinc-500 uppercase">{t('events')}</p>
          </div>
          <div className="rounded-lg bg-zinc-900/50 border border-zinc-800 p-3 text-center">
            <Users className="h-4 w-4 text-blue-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">239</p>
            <p className="text-[10px] text-zinc-500 uppercase">{t('totalFans')}</p>
            <p className="text-[9px] text-blue-400/70 mt-0.5">{t('returning')}</p>
          </div>
          <div className="rounded-lg bg-zinc-900/50 border border-zinc-800 p-3 text-center">
            <DollarSign className="h-4 w-4 text-emerald-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">$449</p>
            <p className="text-[10px] text-zinc-500 uppercase">{t('allTimeTips')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
